import React, {memo, useCallback, useEffect, useMemo, useState} from "react"
import {Button, Card, Checkbox, Col, Modal, Row, Table, Tag} from "antd"
import {center} from "../../../helps/tableHelper"
import moment from "moment/moment"
import * as yup from "yup"
import {useFormik} from "formik"
import {FormSelect} from "../FormSelect"
import {JsonOptionsType} from "./JsonOptionsType"
import {FormInput} from "../FormInput"
import {JsonRulesRadioOptions} from "./JsonRulesRadioOptions"
import {AttributeRuleType} from "../../../types/types"

type PropsTypes = {
    grid: string
    attributes: string
    setAttributes: (value: string) => void
    isInvalid?: string
}
export const JsonFormRules: React.FC<PropsTypes> = memo(({grid, attributes, setAttributes, isInvalid}) => {
    const [data, setData] = useState<Array<any>>([])
    const [attr, setAttr] = useState<any>(null)
    const [schema, setSchema] = useState(yup.object().shape({}))
    const [submitOptionsCallback, setSubmitOptionsCallback] = useState<any>(null)

    const changeAttributes = useCallback((attr: AttributeRuleType): boolean => {
        const parsed: Array<AttributeRuleType> = JSON.parse(attributes)
        if (Array.isArray(parsed)) {
            const time = moment.utc()
            const index = parsed.findIndex(i => i.alias === attr.alias)

            if (index !== -1) parsed[index] = {...attr, updated_at: time}
            else parsed.push({...attr, created_at: time, updated_at: time})

            setAttributes(JSON.stringify(parsed))

            return true
        }
        return false
    }, [attributes])

    const formik = useFormik({
        initialValues: attr,
        validationSchema: schema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            let result = true

            if (submitOptionsCallback) {
                result = await submitOptionsCallback()
            }

            await validateForm(values)

            for (const key in values) {
                await setFieldTouched(key, true)
            }

            if (result && changeAttributes(values)) setAttr(null)
        },
    })
    const {values, handleChange, handleBlur, errors, touched, setFieldValue, setValues, validateForm, setFieldTouched, handleSubmit} = formik

    useEffect(() => {
        return () => {
            setAttr(null)
        }
    }, [])

    useEffect(() => {
        switch (values?.type) {
            case 'text':
            case 'textarea':
            case 'number':
                setSchema(yup.object().shape({
                    name: yup.string().required('Введите название'),
                    length: yup.number().typeError('Это не цифры').required('Введите количество символов').min(1, 'Минимум 1'),
                    pos: yup.number().typeError('Это не цифры').required('Введите позицию в списке').min(1, 'Минимум 1'),
                }))
                break
            case 'radio':
            case 'checkboxes':
                setSchema(yup.object().shape({
                    name: yup.string().required('Введите название'),
                    pos: yup.number().typeError('Это не цифры').required('Введите позицию в списке').min(1, 'Минимум 1'),
                }))
                break
        }
    }, [values?.type])

    const columns = useMemo(() => {
        const parsed: Array<AttributeRuleType> = JSON.parse(attributes)

        return [
            {
                title: 'Тип',
                dataIndex: 'type',
                key: 'type',
                width: 'fit-content',
                align: center,
                render: (type: string) => {
                    return (<div>{JsonOptionsType.find(i => i.value === type)?.label}</div>)
                },
            },
            {
                title: 'Название',
                dataIndex: 'name',
                key: 'name',
                align: center,
            },
            {
                title: 'Позиция',
                dataIndex: 'pos',
                key: 'pos',
                align: center,
            },
            {
                title: 'Обязательный',
                dataIndex: 'required',
                key: 'required',
                render: (required: boolean) => {
                    return (
                        required ? <Tag style={{marginRight: 0}} color="gold">Да</Tag> : <Tag style={{marginRight: 0}} color="default">Нет</Tag>
                    )
                },
                align: center,
            },
            {
                title: 'Показывать в таблице',
                dataIndex: 'showInTable',
                key: 'showInTable',
                render: (showInTable: boolean) => {
                    return (
                        showInTable ? <Tag style={{marginRight: 0}} color="green">Да</Tag> : <Tag style={{marginRight: 0}} color="default">Нет</Tag>
                    )
                },
                align: center,
            },
            {
                title: 'Создан',
                dataIndex: 'created_at',
                key: 'created_at',
                align: center,
            },
            {
                title: 'Обновлен',
                dataIndex: 'updated_at',
                key: 'updated_at',
                align: center,
            },
            {
                title: 'Удалено',
                dataIndex: 'is_deleted',
                key: 'is_deleted',
                render: (deleted: boolean) => {
                    return (
                        deleted ? <Tag style={{marginRight: 0}} color="volcano">Да</Tag> : <Tag style={{marginRight: 0}} color="green">Нет</Tag>
                    )
                },
                align: center,
            },
            {
                title: '',
                dataIndex: 'edit',
                key: 'edit',
                align: center,
                render: (alias: string) => {
                    if (Array.isArray(parsed)) {
                        const attr = parsed.find((i: any) => i.alias === alias)
                        if (attr) {
                            return (
                                <Button onClick={() => setAttr(attr)} type="link" size="middle" style={{marginTop: 5}}>
                                    Редактировать
                                </Button>
                            )
                        }
                    }
                },
            },
        ]
    }, [attributes])

    useEffect(() => {
        const parsed: Array<AttributeRuleType> = JSON.parse(attributes)
        if (Array.isArray(parsed)) {
            const tableData: Array<any> = []

            parsed.forEach((i) => {
                tableData.push({
                    key: i.alias,
                    type: i.type,
                    name: i.name,
                    pos: i.pos,
                    required: i.required,
                    showInTable: i.showInTable,
                    is_deleted: i.is_deleted,
                    created_at: moment(i.created_at).format('DD.MM.YYYY HH:mm'),
                    updated_at: moment(i.updated_at).format('DD.MM.YYYY HH:mm'),
                    edit: i.alias,
                })
            })

            setData(tableData)
        }
    }, [attributes])

    return (
        <div>
            <Row gutter={[24, 0]}>
                <Col xs={24}>
                    <Card variant="borderless"
                          className="criclebox tablespace mb-24"
                          title={
                              <div style={{display: 'flex', alignItems: 'center'}}>
                                  <div className="description-attributes-title" style={{marginBottom: 0}}>Атрибуты объектов</div>
                                  <Button onClick={() => {
                                      const pids: Array<number> = []
                                      const parsed: Array<AttributeRuleType> = JSON.parse(attributes)

                                      if (Array.isArray(parsed)) {
                                          parsed.forEach((i: any) => {
                                              const pid = parseInt(i.alias?.replace(`${grid}_pid`, ''), 10)
                                              if (pid) pids.push(pid)
                                          })
                                      }

                                      let max = Math.max(...pids)
                                      max = max > 0 ? max : 0

                                      setAttr({
                                          name: '',
                                          type: 'text',
                                          alias: `${grid}_pid${max + 1}`,
                                          length: undefined,
                                          mask: '',
                                          is_deleted: false,
                                          pos: undefined,
                                          required: false,
                                          options: [],
                                          showInTable: false,
                                      })
                                  }}
                                          type="link"
                                          size="middle"
                                          style={{marginTop: 5}}
                                  >
                                      Добавить
                                  </Button>

                                  {isInvalid ? <div className="input-error-visible">{isInvalid}</div> : null}
                              </div>
                          }
                    >
                        <div className="table-responsive">
                            <Table columns={columns}
                                   dataSource={data}
                                   className="ant-border-space"
                                   pagination={false}
                            />
                        </div>
                    </Card>
                </Col>
            </Row>

            {
                attr
                    ? <Modal title="Атрибут"
                             centered
                             open={attr}
                             onOk={() => handleSubmit()}
                             onCancel={() => setAttr(null)}
                             width={400}
                             style={{marginTop: 20, marginBottom: 20}}
                    >
                        <FormSelect placeholder="Тип"
                                    value={values?.type}
                                    onChange={(value) => setFieldValue('type', value)}
                                    options={JsonOptionsType}
                                    className="form-select"
                                    disabled={attr.created_at}
                        />
                        <FormInput placeholder="Название"
                                   value={values?.name}
                                   name="name"
                                   onBlur={handleBlur}
                                   onChange={handleChange}
                                   isInvalid={touched.name && errors.name}
                                   allowClear
                        />
                        {
                            values?.type === 'text' || values?.type === 'textarea' || values?.type === 'number'
                                ? <FormInput placeholder="Длина"
                                             value={values?.length}
                                             type="number"
                                             name="length"
                                             onBlur={handleBlur}
                                             onChange={handleChange}
                                             isInvalid={touched?.length && errors?.length}
                                             allowClear
                                />
                                : null
                        }
                        <FormInput placeholder="Позиция"
                                   value={values?.pos}
                                   type="number"
                                   name="pos"
                                   onBlur={handleBlur}
                                   onChange={handleChange}
                                   isInvalid={touched?.pos && errors?.pos}
                                   allowClear
                        />
                        {
                            values?.type === 'radio' || values?.type === 'checkboxes'
                                ? <div>
                                    <div className="description-attributes-options-title">Опции</div>
                                    <JsonRulesRadioOptions setOptions={(options) => setFieldValue('options', options)}
                                                           attributes={JSON.stringify(attr?.options)}
                                                           setSubmitOptionsCallback={setSubmitOptionsCallback}
                                    />
                                </div>
                                : null
                        }
                        <div style={{marginTop: 20}}>
                            <Checkbox checked={values?.required}
                                      name="required"
                                      onChange={handleChange}>
                                <Tag color={values?.required ? 'gold' : 'default'}>Обязательный</Tag>
                            </Checkbox>
                        </div>
                        <div style={{marginTop: 20}}>
                            <Checkbox checked={values?.showInTable}
                                      name="showInTable"
                                      onChange={handleChange}>
                                <Tag color={values?.showInTable ? 'green' : 'default'}>Показывать в таблице</Tag>
                            </Checkbox>
                        </div>
                        <div style={{marginTop: 20}}>
                            <Checkbox checked={values?.is_deleted}
                                      name="is_deleted"
                                      onChange={handleChange}>
                                <Tag color={values?.is_deleted ? 'volcano' : 'default'}>Удалено</Tag>
                            </Checkbox>
                        </div>
                    </Modal>
                    : null
            }
        </div>
    )
})